'use client';

import { useCallback, useEffect, useRef } from 'react';

/* -------------------- 基础工具 -------------------- */

/**
 * 获取 Cookie
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * 设置 Cookie
 */
export function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}`;
}

/**
 * 获取 URL 查询参数
 */
export function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

/* -------------------- fbc / fbp 管理 -------------------- */

/**
 * 初始化 fbc Cookie
 */
function initFbc(): string | null {
  const fbclid = getQueryParam('fbclid');
  if (!fbclid) return null;

  const fbc = `fb.1.${Date.now()}.${fbclid}`;
  setCookie('_fbc', fbc, 7776000); // 90 天
  return fbc;
}

/**
 * 获取 _fbc Cookie
 */
export function getFbc(): string | null {
  return getCookie('_fbc');
}

/**
 * 获取 _fbp Cookie
 */
export function getFbp(): string | null {
  return getCookie('_fbp');
}

/* -------------------- Pixel 状态判断 -------------------- */

declare global {
  interface Window {
    fbq?: {
      loaded?: boolean;
      (...args: unknown[]): void;
    };
  }
}

/**
 * 等待 fbq 准备就绪
 */
export function waitForFbqReady(callback: () => void, timeout = 3000): void {
  if (typeof window === 'undefined') return;

  const start = Date.now();

  const check = () => {
    if (window.fbq && window.fbq.loaded) {
      callback();
    } else if (Date.now() - start < timeout) {
      setTimeout(check, 500);
    } else {
      // console.warn('fbq not ready, timeout');
    }
  };

  check();
}

/* -------------------- React Hook -------------------- */

interface UseMetaPixelResult {
  getFbc: () => string | null;
  getFbp: () => string | null;
  waitForFbqReady: (callback: () => void, timeout?: number) => void;
  isInitialized: boolean;
}

/**
 * Meta Pixel React Hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { getFbc, getFbp, waitForFbqReady, isInitialized } = useMetaPixel();
 *
 *   useEffect(() => {
 *     if (!isInitialized) return;
 *
 *     // 获取 _fbc
 *     const fbc = getFbc();
 *     if (fbc) {
 *       SDK.setCommonStatsParams('fb_fbc', fbc);
 *     }
 *
 *     // 获取 _fbp (需要等待 fbq 加载完成)
 *     waitForFbqReady(() => {
 *       const fbp = getFbp();
 *       if (fbp) {
 *         SDK.setCommonStatsParams('fb_fbp', fbp);
 *       }
 *     });
 *   }, [isInitialized]);
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useMetaPixel(): UseMetaPixelResult {
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) return;

    // 尽早写入 fbc（防止 SPA 丢失）
    initFbc();
    isInitializedRef.current = true;
  }, []);

  const memoizedWaitForFbqReady = useCallback(
    (callback: () => void, timeout?: number) => {
      waitForFbqReady(callback, timeout);
    },
    [],
  );

  return {
    getFbc,
    getFbp,
    waitForFbqReady: memoizedWaitForFbqReady,
    isInitialized: isInitializedRef.current,
  };
}

/* -------------------- Provider 组件 (可选) -------------------- */

interface MetaPixelProviderProps {
  children: React.ReactNode;
  onReady?: (data: { fbc: string | null; fbp: string | null; }) => void;
}

/**
 * Meta Pixel Provider 组件
 * 在应用顶层使用，自动初始化 Meta Pixel
 *
 * @example
 * ```tsx
 * // app/layout.tsx
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <MetaPixelProvider
 *           onReady={({ fbc, fbp }) => {
 *             if (fbc) SDK.setCommonStatsParams('fb_fbc', fbc);
 *             if (fbp) SDK.setCommonStatsParams('fb_fbp', fbp);
 *           }}
 *         >
 *           {children}
 *         </MetaPixelProvider>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function MetaPixelProvider({ children, onReady }: MetaPixelProviderProps) {
  useEffect(() => {
    // 尽早写入 fbc
    initFbc();

    // 获取 fbc
    const fbc = getFbc();

    // 等待 fbq 加载完成后获取 fbp
    waitForFbqReady(() => {
      const fbp = getFbp();
      onReady?.({ fbc, fbp });
    });

    // 如果 fbq 未加载，仍然回调 fbc
    const timeout = setTimeout(() => {
      if (!window.fbq?.loaded) {
        onReady?.({ fbc, fbp: null });
      }
    }, 3500);

    return () => clearTimeout(timeout);
  }, [onReady]);

  return <>{children}</>;
}

export default useMetaPixel;
